import {
  Alert,
  Snackbar,
  Box,
  Button,
  Dialog,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import SelectProduct from '../select/SelectProduct';
import SelectService from '../select/SelectService';
import Sum from './position-modules/Sum';
import { numberToString } from '../special/numberConverter';
import Trans from '../special/Trans';
import { useTranslation } from 'react-i18next';
import ExoDialog from './ExoDialog';
import { PositionsHeader } from './positions/PositionsHeader';
import { PositionsRowEmpty } from './positions/PositionsRowEmpty';
import { PositionImage } from './positions/collapse-modules/PositionImage';
import { PositionNumber } from './positions/collapse-modules/PositionNumber';
import { PositionPreCalculation } from './positions/collapse-modules/PositionPreCalculation';
import { PositionDescription } from './positions/collapse-modules/PositionDescription';
import { PositionDiscount } from './positions/PositionDiscount';
import { PositionCopy } from './positions/collapse-modules/PositionCopy';
import { PositionRebate } from './positions/collapse-modules/PositionRebate';
import { HOAICalculator } from './hoai/HOAICalculator';
import { updateOrCreate } from '../special/updateOrCreate';
import SelectTimeBilling from '../select/SelectTimeBilling';
import SelectTimeBillingEdit from '../select/SelectTimeBillingEdit';
import { DBRequest, API_GET, useConfig } from '../../api/api';
import ExoAlert from './ExoAlert';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Entry from './position-modules/Entry';
import uuid from 'react-uuid';
import { createPortal } from 'react-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { Position } from './positions/Position';
import { round } from '../special/round';
import Controls from './position-modules/Controls';
import { SelectImportPositions } from '../select/SelectImportPositions';
import { applyArrangement, buildSortRequest } from '../ai-chat/positionSort';
import { runAiPositionSort } from '../ai-chat/runAiPositionSort';
import { sortByPositionNumber } from '../special/sortPositions';

// Strip the properties that tie a position to a specific source so a duplicated
// copy doesn't silently inherit them. A duplicate must be a fresh line (no `id`),
// must not stay linked to the original HOAI phase/group (hoai* + phaseNumber),
// and — for an order-based line — should keep the fact that it is settled from
// the order (isOrderPosition + orderId) while dropping the pointer to the exact
// order position it was billed against (orderPositionId) and the ordered/billed
// figures derived from it. timeTrackingIds are dropped too so a copy never
// double-references the same tracked times.
function stripLinkedProperties(position) {
  const {
    id,
    hoaiId,
    hoaiType,
    hoaiPosition,
    hoaiSiemonPosition,
    phaseNumber,
    editableHOAI,
    orderPositionId,
    orderedAmount,
    orderedPrice,
    totalAmount,
    billedAmount,
    billedTotal,
    timeTrackingIds,
    ...rest
  } = position;
  return { ...rest, id: null };
}

const ExoPositions = forwardRef(
  (
    {
      debug,
      className,
      onPositionChange,
      startPositions,
      onFocus = () => {},
      descriptionVars = [],
      showVars,
      hideServices,
      hideProducts,
      hideNewPosition,
      hideSum,
      hideGroups,
      hideDiscount,
      hideHOAI,
      hideTimeBilling = true,
      hidePostPositions = false,
      hideImport,
      // 'offer' | 'order' | 'invoice': offers "Scan document with AI" among
      // the import options, which extracts positions from attached documents
      aiScanDocument,
      project,
      onTimeTrackingDataChange,
      timeTrackingData,
      emptyStart,
      imageType,
      imageApiFilter,
      defaultTaxRate,
      openAmount,
      groupKey = 'name',
      hoaiData,
      title,
      textBlockType,
      skonto,

      collapseModules = [
        { component: PositionImage, key: 'image', label: 'Image' },
        {
          component: PositionDescription,
          key: 'description',
          label: 'Description',
          textBlockType: textBlockType,
        },
        {
          component: PositionNumber,
          key: 'taxRate',
          label: 'Tax Rate',
          unit: '%',
          decimalPlaces: 0,
        },
        {
          component: PositionRebate,
          label: 'Allowance',
        },
        {
          component: PositionCopy,
          label: 'Duplicate position',
        },
        {
          component: PositionPreCalculation,
          key: 'price',
          label: 'Calculation of sales price',
        },
      ],
      columns = [
        {
          text: Trans('Position'),
          width: '80px',
          align: 'left',
          key: 'position',
          className: 'monospace justify-center',
          number: true,
          decimalPlaces: 2,
          editable: false,
          hidden: false,
          default: 1,
        },
        {
          text: Trans('Description'),
          width: 'auto',
          align: 'left',
          key: 'name',
          editable: true,
          hidden: false,
          default: '',
        },
        {
          text: Trans('Amount'),
          width: '140px',
          align: 'center',
          key: 'amount',
          className: 'monospace',
          number: true,
          decimalPlaces: 2,
          editable: true,
          hidden: false,
          default: 0,
        },
        {
          text: Trans('Unit Price'),
          width: '120px',
          align: 'center',
          key: 'price',
          className: 'monospace',
          unit: ' €',
          number: true,
          editable: true,
          hidden: false,
          default: 0,
        },
        {
          text: '',
          key: 'delete',
          width: '37px',
        },
        {
          text: Trans('Sum'),
          width: '130px',
          align: 'right',
          key: 'custom',
          unit: ' €',
          className: 'justify-end',
          hideOnMobile: true,
          hidden: false,
          custom: (element, unit) => {
            var sum = 0;
            if (element.amount && element.price) {
              sum = round(
                parseFloat(element.amount) * parseFloat(element.price),
              );
            }
            return (
              <Box className="w-full text-right monospace">
                {element.isOptional && '('}
                {numberToString(sum)}
                {unit}
                {element.isOptional && ')'}
              </Box>
            );
          },
        },
        {
          key: 'status',
          hidden: true,
          default: 0,
        },
        {
          key: 'description',
          hidden: true,
          default: '',
        },
        {
          key: 'positionableId',
          hidden: true,
          default: null,
        },
        {
          key: 'positionableType',
          hidden: true,
          default: 'custom',
        },
      ],
    },
    ref,
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const config = useConfig();

    const virtuosoRef = useRef();

    const { t } = useTranslation();

    const [items, setItems] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [defaultPosition, setDefaultPosition] = useState(null);
    const [openHOAI, setOpenHOAI] = useState(null);
    const [hoais, setHoais] = useState(hoaiData ?? []);
    const [openTimeBilling, setOpenTimeBilling] = useState(null);
    const [openTimeBillingEdit, setOpenTimeBillingEdit] = useState(false);
    const [selectedTimeBillingPosition, setSelectedTimeBillingPosition] =
      useState(null);
    const [openImport, setOpenImport] = useState(false);
    // "Sort with AI": runs in the background, the rows are rebuilt when it ends
    const [aiSorting, setAiSorting] = useState(false);
    const [aiSortNotice, setAiSortNotice] = useState(null); // { severity, text }
    const itemsRef = useRef([]);
    const [timeBillingWarning, setTimeBillingWarning] = useState(false);

    const posColWidth = 15; //In pixels, one pixel is added for the border

    const [dynamicColumns, setDynamicColumns] = useState([...columns]);

    const [sortIds, setSortIds] = useState([]);
    const [activeItem, setActiveItem] = useState(null);
    const [projectedIndex, setProjectedIndex] = useState(null);
    const [horizontalDelta, setHorizontalDelta] = useState(0);

    const [alertState, setAlertState] = useState({
      alertOpen: false,
      item: null,
    });

    function checkSpecialPositions(pos) {
      //When adjusting make sure to adjust in Position.jsx as well
      return pos.isPostPosDivider || pos.isLastPlaceholder || pos.isSum;
    }

    // Reconstruct timeTrackingData from positions if it doesn't exist
    useEffect(() => {
      if (timeTrackingData || !onTimeTrackingDataChange) return;

      const timeBillingPositions = allItems.filter(
        (item) =>
          item.timeTrackingIds &&
          Array.isArray(item.timeTrackingIds) &&
          item.timeTrackingIds.length > 0,
      );

      if (timeBillingPositions.length === 0) return;

      const allIds = [
        ...new Set(timeBillingPositions.flatMap((p) => p.timeTrackingIds)),
      ];

      // Fetch actual time entries to calculate real hours
      DBRequest({
        config,
        path: `timetrackings?ids=${allIds.join(',')}`,
        method: API_GET,
        onResponse: (res) => {
          const timeEntries = res.data || [];

          const wagePresets = timeBillingPositions
            .filter((p) => p.wagePresetId)
            .map((p) => {
              // Calculate actual hours from time entries for this position
              const positionEntries = timeEntries.filter((tt) =>
                (p.timeTrackingIds || []).includes(tt.id),
              );
              const actualHours = positionEntries.reduce((sum, tt) => {
                const timeStart = tt.timeStart || tt.time_start;
                const timeEnd = tt.timeEnd || tt.time_end;
                if (!timeStart || !timeEnd) return sum;
                const start = new Date(timeStart);
                const end = new Date(timeEnd);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return sum;
                return sum + (end - start) / (1000 * 60 * 60);
              }, 0);

              return {
                id: p.wagePresetId,
                label: p.name || '',
                wage: parseFloat(p.price) || 0,
                hours: parseFloat(actualHours.toFixed(2)),
                timeTrackingIds: p.timeTrackingIds,
              };
            });

          const reconstructedData = {
            ids: allIds,
            adjustments: {},
            billingConfig: {
              mode: wagePresets.length > 0 ? 'chr' : 'positions',
              rounding: null,
              startDate: null,
              endDate: null,
              wagePresets,
            },
          };

          onTimeTrackingDataChange(reconstructedData);
          setTimeBillingWarning(true);
        },
      });
    }, [timeTrackingData, onTimeTrackingDataChange]);

    function handlePositionChange(positions, hoaiData) {
      const cleanedPos = positions.filter((el) => !checkSpecialPositions(el));

      // Sync wagePresets wages with position prices when a time-billing position price changes
      if (
        timeTrackingData?.billingConfig?.wagePresets &&
        onTimeTrackingDataChange
      ) {
        const wagePresets = timeTrackingData.billingConfig.wagePresets;
        let needsUpdate = false;

        const updatedPresets = wagePresets.map((preset) => {
          // Match by wagePresetId (CHR mode) or timeTrackingIds overlap (positions mode)
          const matchingPos = cleanedPos.find((pos) => {
            if (pos.wagePresetId && preset.id) {
              return String(pos.wagePresetId) === String(preset.id);
            }
            if (
              pos.timeTrackingIds?.length > 0 &&
              preset.timeTrackingIds?.length > 0
            ) {
              return pos.timeTrackingIds.some((id) =>
                preset.timeTrackingIds.includes(id),
              );
            }
            return false;
          });

          if (matchingPos && matchingPos.price !== preset.wage) {
            needsUpdate = true;
            return { ...preset, wage: matchingPos.price };
          }
          return preset;
        });

        if (needsUpdate) {
          onTimeTrackingDataChange({
            ...timeTrackingData,
            billingConfig: {
              ...timeTrackingData.billingConfig,
              wagePresets: updatedPresets,
            },
          });
        }
      }

      onPositionChange(cleanedPos, hoaiData);
      debug && console.log('Positions changed:', cleanedPos, hoaiData);
    }

    function checkSortCorrectness(items) {
      const expected = new Set(
        Array.from({ length: items.length }, (_, i) => i + 1),
      );
      const actual = new Set(items.map((obj) => obj.sortId));

      return (
        expected.size === actual.size &&
        [...expected].every((id) => actual.has(id))
      );
    }

    function migrateLegacyPositions(items) {
      let seenGroup = false;
      const newItems = items;
      for (let i = 0; i < items.length; i++) {
        if (newItems[i].depth == null) {
          if (newItems[i].isGroup) {
            seenGroup = true;
            newItems[i].depth = 0;
          } else {
            if (seenGroup) {
              newItems[i].depth = 1;
            } else {
              newItems[i].depth = 0;
            }
          }
        }
      }
      return newItems;
    }

    function createSortIds(newItems) {
      let update = false;

      // The position number is the document order. The numbering is derived
      // from the array order below, so bring the array in line with it first.
      newItems = sortByPositionNumber(newItems);

      if (
        !hidePostPositions &&
        newItems.findIndex((el) => el.isPostPosDivider) == -1
      ) {
        update = true;

        const lastNonPostPosIndex = newItems.reduce(
          (lastIndex, el, idx) =>
            el.isPostPosition === false ? idx : lastIndex,
          -1,
        );

        const insertIndex =
          lastNonPostPosIndex === -1 ? 0 : lastNonPostPosIndex + 1;

        newItems = [
          ...newItems.slice(0, insertIndex),
          {
            isPostPosDivider: true,
            keyId: 'PostPosDivider',
            depth: 0,
          },
          ...newItems.slice(insertIndex),
        ];
      }

      if (newItems.findIndex((el) => el.isLastPlaceholder) == -1) {
        update = true;

        newItems = [
          ...newItems,
          {
            isLastPlaceholder: true,
            keyId: 'LastPlaceholder',
            depth: 0,
          },
        ];
      }

      if (newItems.findIndex((el) => el.isSum) == -1) {
        update = true;

        newItems = [
          ...newItems,
          {
            isSum: true,
            keyId: 'sumPos',
            depth: 0,
          },
        ];
      }

      if (newItems.some((item) => !('depth' in item))) {
        newItems = migrateLegacyPositions(newItems);
        update = true;
      }

      // Set editableHOAI flag for HOAI group positions loaded from the backend,
      // and expand HOAI groups so their phases are shown. Without this a group
      // can load collapsed (a persisted/absent expandStatus), which hides the
      // phase rows even though they still count toward the totals. Gated on
      // !editableHOAI so it runs once on load and doesn't override a runtime
      // collapse on later edits.
      if (
        newItems.some(
          (item) =>
            item.isGroup &&
            item.hoaiId &&
            item.hoaiType === 'group' &&
            !item.editableHOAI,
        )
      ) {
        newItems = newItems.map((item) => {
          if (!item.isGroup) return item;
          return {
            ...item,
            expandStatus: 2,
            ...(item.hoaiId &&
              item.hoaiType === 'group' && { editableHOAI: true }),
          };
        });
        update = true;
      }

      if (!checkSortCorrectness(newItems)) {
        newItems = newItems.map((item, index) => {
          return { ...item, sortId: index + 1 };
        });
        update = true;
      }

      newItems = newItems.map((item) => {
        if (!item.keyId) update = true;
        let newKeyId = item.keyId ?? item.id ?? uuid();
        if (!isNaN(newKeyId)) {
          newKeyId = 'keyId' + newKeyId;
        }
        return { ...item, keyId: newKeyId };
      });

      setItems(calculateNewPositions(newItems, true));

      const newSortItems = newItems.map((item) => {
        return item.sortId;
      });

      setSortIds(newSortItems);
    }

    const [sum, setSum] = useState({
      netto: 0,
      tax: 0,
    });

    function recalculateColumns(maxDepth) {
      let newColumns = columns;

      const index = newColumns.findIndex((el) => el.key == 'position');

      if (index == -1) {
        return;
      }

      newColumns[index] = {
        ...newColumns[index],
        key: 'position0',
        colSpan: maxDepth + 1,
        width: `${posColWidth}px`,
      };

      for (let i = 1; i <= maxDepth; i++) {
        newColumns.splice(index + i, 0, {
          key: 'position' + i,
          width: i == maxDepth ? `${maxDepth * 8}px` : `${posColWidth}px`,
        });
      }

      setDynamicColumns(newColumns);
    }

    useEffect(() => {
      if (startPositions && startPositions.length > 0)
        createSortIds(startPositions);
    }, [startPositions]);

    useEffect(() => {
      var newDefaultPositions = {};
      columns.forEach((column) => {
        if (!column.key.match(/position\d+/)) {
          newDefaultPositions[column.key] = column.default;
        }
      });
      newDefaultPositions.taxRate =
        defaultTaxRate === undefined || defaultTaxRate === null
          ? 19
          : defaultTaxRate;
      newDefaultPositions.depth = 0;
      newDefaultPositions.type = 'default';

      setDefaultPosition(newDefaultPositions);
      if (!(startPositions && startPositions.length)) createSortIds(items);
    }, [defaultTaxRate]);

    useEffect(() => {
      if (emptyStart) return;
      if (items.length === 2 + (hidePostPositions ? 0 : 1) && defaultPosition) {
        handleNewPosition(defaultPosition);
      }
    }, [defaultPosition]);

    function moveGroupSortIds(sortIds, groupSortIds, fromIndex, toIndex) {
      const filtered = sortIds.filter((id) => !groupSortIds.includes(id));

      const insertIndex =
        toIndex >= fromIndex ? toIndex - groupSortIds.length + 1 : toIndex;

      return [
        ...filtered.slice(0, insertIndex),
        ...groupSortIds,
        ...filtered.slice(insertIndex),
      ];
    }

    function handleOnDragMove(event) {
      setHorizontalDelta(event.delta.x);
    }

    const handleOnDragStart = (event) => {
      const itemIndex = items.findIndex(
        (item) => item.sortId === event.active.id,
      );
      setActiveItem(
        itemIndex != -1 ? { ...items[itemIndex], index: itemIndex } : null,
      );
      if (items[itemIndex].isGroup) {
        const newItems = items;
        newItems[itemIndex].expandStatus = 1;
        setItems(calculateNewPositions(newItems));
      }
    };

    function handleOnDragOver(event) {
      const overId = event.over?.id;
      if (!overId) return null;
      const overIndex = items.findIndex((item) => item.sortId === overId);
      if (
        (activeItem.isGroup || activeItem.type == 'hoaiPhase') &&
        items[overIndex].isPostPosition
      ) {
        return;
      }

      setProjectedIndex(overIndex === -1 ? null : overIndex);
    }

    const handleOnDragEnd = (event) => {
      const { active, over, delta } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sortIds.findIndex((id) => id === active.id);
      let newIndex = sortIds.findIndex((id) => id === over.id);

      let addGroup = false;
      if (
        (activeItem.isGroup || activeItem.type == 'hoaiPhase') &&
        allItems[newIndex].isPostPosition
      ) {
        newIndex = projectedIndex;
        addGroup = activeItem.isGroup;
      }

      newIndex = newIndex >= oldIndex ? newIndex - 1 : newIndex;

      let groupSortIds = [allItems[oldIndex].sortId];
      if (allItems[oldIndex].isGroup && allItems[oldIndex].expandStatus > 0) {
        let counter = 1;
        while (allItems[oldIndex + counter].depth > allItems[oldIndex].depth) {
          groupSortIds.push(allItems[oldIndex + counter].sortId);
          counter++;
        }
      }

      const newSortIds =
        groupSortIds.length > 1
          ? moveGroupSortIds(
              sortIds,
              groupSortIds,
              oldIndex,
              addGroup ? newIndex + groupSortIds.length - 1 : newIndex,
            )
          : arrayMove(sortIds, oldIndex, newIndex);
      setSortIds(newSortIds);

      const reorderedItems = newSortIds.map((sortId) =>
        allItems.find((item) => item.sortId === sortId),
      );

      newIndex = reorderedItems.findIndex((item) => item.sortId === active.id);

      const oldDepth = reorderedItems[newIndex].depth;

      reorderedItems[newIndex].depth = calculateDepthFromDelta(
        delta.x,
        reorderedItems[newIndex].depth,
        [
          newIndex == 0
            ? { isGroup: false, depth: 0 }
            : reorderedItems[newIndex - 1],
          newIndex - 1 >= reorderedItems.length
            ? { isGroup: false, depth: 0 }
            : reorderedItems[newIndex + groupSortIds.length],
        ],
      );

      for (let i = 1; i < groupSortIds.length; i++) {
        reorderedItems[newIndex + i].depth +=
          reorderedItems[newIndex].depth - oldDepth;
      }

      if (activeItem.isGroup) {
        reorderedItems[newIndex].expandStatus = activeItem.expandStatus;
      }

      setActiveItem(null);
      setProjectedIndex(null);

      setItems(calculateNewPositions(reorderedItems, true, true));

      setHorizontalDelta(0);
    };

    function handleNewPosition(position, afterDivider, afterPosition) {
      if (position) {
        // stripLinkedProperties keeps freshly-created positions (from the add
        // buttons / allowance) untouched — they carry none of these fields — while
        // ensuring the "Duplicate position" button, which passes a full existing
        // position, produces a clean copy instead of inheriting its source links.
        position = {
          ...stripLinkedProperties(position),
          keyId: uuid(),
          sortId: null,
          depth: afterPosition ? afterPosition.depth : 0,
          expand: position.isGroup ? null : false,
        };
      }

      const maxSortId = Math.max(...allItems.map((el) => el.sortId));

      let givenIndex;
      if (afterPosition) {
        const afterIndex = allItems.findIndex(
          (el) => el.keyId === afterPosition.keyId,
        );
        if (afterIndex >= 0) {
          givenIndex = afterIndex + 1;
        }
      }

      const newOrderedPositions = insertPositions(
        allItems,
        [
          {
            ...{ ...defaultPosition, keyId: uuid() },
            ...position,
            sortId: maxSortId + 1,
          },
        ],
        [maxSortId + 1],
        afterDivider || position.isPostPosition,
        givenIndex,
      );

      setItems(newOrderedPositions);

      virtuosoRef.current?.scrollToIndex({
        index: givenIndex
          ? givenIndex
          : afterDivider
            ? newOrderedPositions.length
            : newOrderedPositions.findIndex((el) => el.isPostPosDivider) + 1,
        align: 'center',
      });
    }

    function handleNewGroup() {
      var newGroup = {
        [groupKey]: '',
        isGroup: true,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
      };
      handleNewPosition(newGroup);
    }

    function calculateNewPositions(
      positions,
      updateAllItems,
      triggerPositionChange,
    ) {
      let newAllItems =
        allItems.length == 0 || updateAllItems ? positions : allItems;
      let newPositions = newAllItems;

      let maxDepth = Math.max(...newPositions.map((obj) => obj.depth)) + 1;
      if (isNaN(maxDepth) || maxDepth < 0) maxDepth = 1;

      const counters = Array(maxDepth).fill(0);
      let currentDepth = 0;

      newPositions.forEach((pos) => {
        if (!pos.isPostPosDivider) {
          if (currentDepth == pos.depth) {
            counters[pos.depth]++;
          } else if (currentDepth < pos.depth) {
            currentDepth = pos.depth;
            counters[pos.depth]++;
          } else if (currentDepth > pos.depth) {
            currentDepth = pos.depth;
            counters[pos.depth]++;
            counters.fill(0, pos.depth + 1);
          }

          pos.position = counters.filter((value) => value !== 0).join('.');
        }
      });

      //Set isPostPosition for all Positions
      let isBeforeDivider = true;
      for (let i = 0; i < newPositions.length; i++) {
        if (isBeforeDivider) {
          newPositions[i].isPostPosition = false;
          if (newPositions[i].isPostPosDivider) {
            isBeforeDivider = false;
          }
        } else {
          newPositions[i].isPostPosition = true;
        }
      }

      let subtotalsBrutto = Array(maxDepth).fill(0);
      let subtotalsNetto = Array(maxDepth).fill(0);
      let prevDepth = 0;
      let percentPosToUpdate = [];
      let outerSumNetto = 0;
      let outerSumBrutto = 0;
      let totalNetto = 0;
      let totalBrutto = 0;

      for (let i = newPositions.length - 1; 0 <= i; i--) {
        const currentPos = newPositions[i];

        if (prevDepth > currentPos.depth && currentPos.isGroup) {
          newPositions[i].expandStatus =
            currentPos.expandStatus == 0 || currentPos.expandStatus == null
              ? 2
              : currentPos.expandStatus;
        } else if (currentPos.isGroup) {
          newPositions[i].expandStatus = 0;
        }
        prevDepth = newPositions[i].depth;

        if (currentPos.type == 'percent') {
          percentPosToUpdate.push(i);
        } else if (currentPos.isGroup) {
          let additionalPercentPriceNetto = 0;
          let additionalPercentPriceBrutto = 0;
          for (let k = percentPosToUpdate.length - 1; k >= 0; k--) {
            if (
              newPositions[percentPosToUpdate[k]].depth - 1 ==
              currentPos.depth
            ) {
              newPositions[percentPosToUpdate[k]].price =
                subtotalsNetto[currentPos.depth];
              additionalPercentPriceNetto += round(
                subtotalsNetto[currentPos.depth] *
                  newPositions[percentPosToUpdate[k]].amount,
              );
              additionalPercentPriceBrutto += round(
                subtotalsBrutto[currentPos.depth] *
                  newPositions[percentPosToUpdate[k]].amount,
              );
              newPositions[percentPosToUpdate[k]].bruttoPrice =
                subtotalsBrutto[currentPos.depth];

              newPositions[percentPosToUpdate[k]].taxRate = Math.round(
                (subtotalsBrutto[currentPos.depth] /
                  subtotalsNetto[currentPos.depth] -
                  1) *
                  100,
              );

              percentPosToUpdate = percentPosToUpdate.filter(
                (el) => el != percentPosToUpdate[k],
              );
            }
          }

          if (newPositions[i].isPostPosition) {
            totalNetto += additionalPercentPriceNetto;
            totalBrutto += additionalPercentPriceBrutto;
          } else {
            outerSumNetto += additionalPercentPriceNetto;
            outerSumBrutto += additionalPercentPriceBrutto;
          }
          newPositions[i].sumNetto =
            subtotalsNetto[currentPos.depth] + additionalPercentPriceNetto;

          newPositions[i].sumBrutto =
            subtotalsBrutto[currentPos.depth] + additionalPercentPriceBrutto;

          subtotalsNetto[currentPos.depth] = 0;
          subtotalsBrutto[currentPos.depth] = 0;

          for (let j = 0; j < currentPos.depth; j++) {
            subtotalsBrutto[j] += additionalPercentPriceBrutto;
            subtotalsNetto[j] += additionalPercentPriceNetto;
          }
        } else if (!currentPos.isOptional) {
          for (let i = 0; i < currentPos.depth; i++) {
            subtotalsNetto[i] += round(
              Number(currentPos.amount) * Number(currentPos.price),
            );
            subtotalsBrutto[i] += round(
              round(Number(currentPos.amount) * Number(currentPos.price)) *
                (1 + currentPos.taxRate / 100),
            );
          }
          if (currentPos.isPostPosition) {
            totalBrutto += round(
              round(
                Number(currentPos.amount ?? 0) * Number(currentPos.price ?? 0),
              ) *
                (1 + (currentPos.taxRate ?? 0) / 100),
            );

            totalNetto += round(
              Number(currentPos.amount ?? 0) * Number(currentPos.price ?? 0),
            );
          } else {
            outerSumBrutto += round(
              Number(currentPos.amount ?? 0) *
                Number(currentPos.price ?? 0) *
                (1 + (currentPos.taxRate ?? 0) / 100),
            );

            outerSumNetto += round(
              Number(currentPos.amount ?? 0) * Number(currentPos.price ?? 0),
            );
          }
        }
      }

      totalNetto += outerSumNetto;
      totalBrutto += outerSumBrutto;

      let preDividerPercentSumNetto = 0;
      let preDividerPercentSumBrutto = 0;

      for (let k = percentPosToUpdate.length - 1; k >= 0; k--) {
        if (!newPositions[percentPosToUpdate[k]].isPostPosition) {
          newPositions[percentPosToUpdate[k]].price = outerSumNetto;
          newPositions[percentPosToUpdate[k]].bruttoPrice = outerSumBrutto;
          totalNetto += round(
            outerSumNetto * newPositions[percentPosToUpdate[k]].amount,
          );
          preDividerPercentSumNetto += round(
            outerSumNetto * newPositions[percentPosToUpdate[k]].amount,
          );
          totalBrutto += round(
            outerSumBrutto * newPositions[percentPosToUpdate[k]].amount,
          );
          preDividerPercentSumBrutto += round(
            outerSumBrutto * newPositions[percentPosToUpdate[k]].amount,
          );

          newPositions[percentPosToUpdate[k]].taxRate = Math.round(
            (outerSumBrutto / outerSumNetto - 1) * 100,
          );
        } else {
          newPositions[percentPosToUpdate[k]].price =
            outerSumNetto + preDividerPercentSumNetto;
          newPositions[percentPosToUpdate[k]].bruttoPrice =
            outerSumBrutto + preDividerPercentSumBrutto;

          totalNetto += round(
            newPositions[percentPosToUpdate[k]].price *
              newPositions[percentPosToUpdate[k]].amount,
          );
          totalBrutto += round(
            newPositions[percentPosToUpdate[k]].bruttoPrice *
              newPositions[percentPosToUpdate[k]].amount,
          );

          newPositions[percentPosToUpdate[k]].taxRate = Math.round(
            (round(
              newPositions[percentPosToUpdate[k]].bruttoPrice *
                newPositions[percentPosToUpdate[k]].amount,
            ) /
              round(
                newPositions[percentPosToUpdate[k]].price *
                  newPositions[percentPosToUpdate[k]].amount,
              ) -
              1) *
              100,
          );
        }
        percentPosToUpdate = percentPosToUpdate.filter(
          (el) => el != percentPosToUpdate[k],
        );
      }

      //Calculate Sum for PostPosDivider
      let sum = 0;
      for (let i = 0; i < newPositions.length; i++) {
        if (newPositions[i].depth == 0) {
          if (newPositions[i].isPostPosDivider) {
            newPositions[i].sumNetto = sum;
            break;
          }
          if (!newPositions[i].isOptional) {
            sum += newPositions[i].isGroup
              ? newPositions[i].sumNetto
              : round(newPositions[i].price * newPositions[i].amount);
          }
        }
      }

      setSum({ netto: totalNetto, tax: totalBrutto - totalNetto });

      //Calculate whether the current element should be shown based on the groups expand status
      let currentExpandDepth = 0;
      for (let i = 0; i < newPositions.length; i++) {
        const currentPos = newPositions[i];
        if (currentExpandDepth < currentPos.depth) {
          newPositions[i].hidden = true;
        } else {
          newPositions[i].hidden = false;
        }
        if (!currentPos.hidden) {
          if (currentPos.isGroup && currentPos.expandStatus == 2) {
            currentExpandDepth = currentPos.depth + 1;
          } else {
            currentExpandDepth = currentPos.depth;
          }
        }
      }
      newPositions = newPositions.filter((el) => !el.hidden);

      if (triggerPositionChange) handlePositionChange(newAllItems);

      setAllItems(newAllItems);

      recalculateColumns(maxDepth);

      return newPositions;
    }

    function insertPositions(
      items,
      insertedItems,
      insertedSortIds,
      afterDivider,
      givenIndex,
    ) {
      const postPosDividerIndex = items.findIndex((el) => el.isPostPosDivider);

      const index =
        givenIndex ??
        (postPosDividerIndex >= 0 && !afterDivider ? postPosDividerIndex : -2);

      const newSortIds = [
        ...sortIds.slice(0, index),
        ...insertedSortIds,
        ...sortIds.slice(index),
      ];

      const newItems = [
        ...items.slice(0, index),
        ...insertedItems,
        ...items.slice(index),
      ];

      setSortIds(newSortIds);
      return calculateNewPositions(newItems, true, true);
    }

    // Helper to update timeTrackingData when positions are deleted
    function updateTimeTrackingDataOnDelete(deletedItems) {
      if (!timeTrackingData || !onTimeTrackingDataChange) return;

      // Collect all timeTrackingIds from deleted items
      const deletedTimeTrackingIds = [];
      deletedItems.forEach((item) => {
        if (item.timeTrackingIds && Array.isArray(item.timeTrackingIds)) {
          deletedTimeTrackingIds.push(...item.timeTrackingIds);
        }
      });

      if (deletedTimeTrackingIds.length === 0) return;

      // Create new timeTrackingData without the deleted IDs
      const newIds = (timeTrackingData.ids || []).filter(
        (id) =>
          !deletedTimeTrackingIds.includes(id) &&
          !deletedTimeTrackingIds.includes(String(id)),
      );

      // Update adjustments object (remove entries for deleted IDs)
      const oldAdjustments = timeTrackingData.adjustments || {};
      const newAdjustments = {};
      newIds.forEach((id) => {
        const adj = oldAdjustments[id] || oldAdjustments[String(id)];
        if (adj) {
          newAdjustments[id] = adj;
        }
      });

      // Update billingConfig wagePresets
      const billingConfig = timeTrackingData.billingConfig || {};
      const newWagePresets = (billingConfig.wagePresets || [])
        .map((preset) => ({
          ...preset,
          timeTrackingIds: (preset.timeTrackingIds || []).filter(
            (id) =>
              !deletedTimeTrackingIds.includes(id) &&
              !deletedTimeTrackingIds.includes(String(id)),
          ),
        }))
        .filter((preset) => preset.timeTrackingIds.length > 0);

      // If no IDs left, clear timeTrackingData
      if (newIds.length === 0) {
        onTimeTrackingDataChange(null);
        return;
      }

      const newTimeTrackingData = {
        ...timeTrackingData,
        ids: newIds,
        adjustments: newAdjustments,
        billingConfig: {
          ...billingConfig,
          wagePresets: newWagePresets,
        },
      };

      onTimeTrackingDataChange(newTimeTrackingData);
    }

    function handleDelete(item) {
      if (item.isGroup) {
        if (item.expandStatus == 0) {
          handleGroupDelete(item, false);
        } else {
          setAlertState({ alertOpen: true, item: item });
        }
      } else {
        // Update timeTrackingData before deleting
        updateTimeTrackingDataOnDelete([item]);

        const newSortIds = sortIds.filter((id) => id != item.sortId);
        const newItems = allItems.filter((el) => el.keyId != item.keyId);
        setItems(calculateNewPositions(newItems, true, true));
        setSortIds(newSortIds);
      }
    }

    function handleDuplicate(item) {
      // Check if item can be duplicated (no HOAI, no time billing, no percent)
      const canDuplicateItem = (it) => {
        return (
          !it.hoaiId &&
          (!it.timeTrackingIds || it.timeTrackingIds.length === 0) &&
          it.type !== 'percent' &&
          it.type !== 'percentEditable' &&
          it.type !== 'hoaiPhase'
        );
      };

      // For non-group items, check if they can be duplicated
      if (!item.isGroup && !canDuplicateItem(item)) {
        return;
      }

      const originalIndex = allItems.findIndex((el) => el.keyId === item.keyId);

      if (item.isGroup) {
        // Find all children of the group
        const groupDepth = item.depth;
        const childrenEndIndex = allItems.findIndex(
          (el, idx) => idx > originalIndex && el.depth <= groupDepth,
        );
        const endIndex =
          childrenEndIndex === -1 ? allItems.length : childrenEndIndex;

        // Get all items in the group (including the group itself)
        const groupItems = allItems.slice(originalIndex, endIndex);

        // Filter: only duplicate group + items that can be duplicated
        const itemsToDuplicate = groupItems.filter(
          (it, idx) => idx === 0 || canDuplicateItem(it),
        );

        // If only the group remains (no valid children), don't duplicate
        if (itemsToDuplicate.length === 1 && !canDuplicateItem(item)) {
          return;
        }

        // Create duplicated items with new IDs
        const duplicatedItems = [];
        const newSortIdsToAdd = [];

        itemsToDuplicate.forEach((it) => {
          const newKeyId = uuid();
          const newSortId = uuid();
          duplicatedItems.push({
            ...stripLinkedProperties(it),
            keyId: newKeyId,
            sortId: newSortId,
          });
          newSortIdsToAdd.push(newSortId);
        });

        // Insert duplicated items after the original group (after all its children)
        const newItems = [
          ...allItems.slice(0, endIndex),
          ...duplicatedItems,
          ...allItems.slice(endIndex),
        ];

        // Find the sort index of the last item in the original group
        const lastGroupItem = groupItems[groupItems.length - 1];
        const lastSortIndex = sortIds.findIndex(
          (id) => id === lastGroupItem.sortId,
        );
        const newSortIds = [
          ...sortIds.slice(0, lastSortIndex + 1),
          ...newSortIdsToAdd,
          ...sortIds.slice(lastSortIndex + 1),
        ];

        setSortIds(newSortIds);
        setItems(calculateNewPositions(newItems, true, true));
      } else {
        // Single item duplication
        const newKeyId = uuid();
        const newSortId = uuid();

        const duplicatedItem = {
          ...stripLinkedProperties(item),
          keyId: newKeyId,
          sortId: newSortId,
        };

        const newItems = [
          ...allItems.slice(0, originalIndex + 1),
          duplicatedItem,
          ...allItems.slice(originalIndex + 1),
        ];

        const originalSortIndex = sortIds.findIndex((id) => id === item.sortId);
        const newSortIds = [
          ...sortIds.slice(0, originalSortIndex + 1),
          newSortId,
          ...sortIds.slice(originalSortIndex + 1),
        ];

        setSortIds(newSortIds);
        setItems(calculateNewPositions(newItems, true, true));
      }
    }

    function handleGroupDelete(item, deleteChildren) {
      setAlertState({ alertOpen: false, item: null });
      let newItems = allItems;
      let newSortIds = sortIds;

      const index = allItems.findIndex((el) => el.keyId == item.keyId);

      let groupKeys = [item.keyId];
      let groupSortIds = [item.sortId];
      if (item.expandStatus > 0) {
        let counter = 1;
        while (allItems[index + counter].depth > allItems[index].depth) {
          groupKeys.push(allItems[index + counter].keyId);
          groupSortIds.push(allItems[index + counter].sortId);
          counter++;
        }
      }

      if (deleteChildren) {
        // Collect items to be deleted for timeTrackingData update
        const itemsToDelete = allItems.filter((el) =>
          groupKeys.includes(el.keyId),
        );
        updateTimeTrackingDataOnDelete(itemsToDelete);

        newItems = newItems.filter((el) => !groupKeys.includes(el.keyId));
        newSortIds = newSortIds.filter((id) => !groupSortIds.includes(id));
      } else {
        // Only delete the group itself, not children - update timeTrackingData for group only
        updateTimeTrackingDataOnDelete([item]);

        newItems = newItems.map((el) => {
          return groupKeys.includes(el.keyId)
            ? { ...el, depth: el.depth - 1 }
            : el;
        });
        newSortIds = newSortIds.filter((id) => id != item.sortId);
        newItems = newItems.filter((el) => el.keyId != item.keyId);
      }
      setSortIds(newSortIds);
      setItems(calculateNewPositions(newItems, true, true));
    }

    // product
    const [openProducts, setOpenProducts] = useState(null);
    function handleProductSelect(product, afterDivider) {
      const productEntry = {
        productNr: product.nr,
        status: 0,
        position: 1,
        name: product.name,
        image: product.image,
        description: product.description,
        amount:
          product.type === 'percent' || product.type === 'percentEditable'
            ? (product.amount ?? 0) / 100
            : product.amount ?? 0,
        unit: product.unit,
        price: product.price,
        taxRate: product.taxRate,
        positionableId: product.id,
        positionableType: 'product',
        taskTitle: product.name,
        pricePerHour: product.price,
        monthlyLimit: 0,
        type: product.type || 'default',
      };
      handleNewPosition(productEntry, afterDivider);
    }

    // service
    const [openServices, setOpenServices] = useState(null);
    function handleServiceSelect(service, afterDivider) {
      const serviceEntry = {
        serviceNr: service.nr,
        status: 0,
        position: 1,
        name: service.name,
        image: service.image,
        description: service.description,
        amount:
          service.type === 'percent' || service.type === 'percentEditable'
            ? (service.amount ?? 0) / 100
            : service.amount ?? 0,
        unit: service.unit,
        price: service.price,
        taxRate: service.taxRate,
        positionableId: service.id,
        positionableType: 'service',
        taskTitle: service.name,
        pricePerHour: service.price,
        monthlyLimit: 0,
        type: service.type || 'default',
      };
      handleNewPosition(serviceEntry, afterDivider);
    }

    // exposed functions
    useImperativeHandle(ref, () => ({
      setItems: setItems,
      items: items,
    }));

    // discount
    const [openDiscount, setOpenDiscount] = useState(false);
    function handleNewDiscount(newDiscount) {
      handleNewPosition({
        ...defaultPosition,
        price: newDiscount.discount * -1,
        amount: 1,
        name: newDiscount.name,
      });
    }

    // hoai
    function removeHOAIPositions(hoaiId) {
      return allItems.filter((item) => item.hoaiId != hoaiId);
    }

    function parseAdditionalCostsFromExoPositions(exoPositions) {
      const find = (type) => exoPositions.find((p) => p.hoaiType === type);
      const surchargePos = find('surcharge');
      const timeFeePos = find('timeFee');
      const specialServicesPos = find('specialServices');
      const additionalServicesPos = find('additionalServices');
      const incidentalCostsPos = find('incidentalCosts');

      return {
        ...(surchargePos && {
          surcharge: Number(surchargePos.amount) * 100,
          surchargeTitle: surchargePos.name,
          surchargeDescription: surchargePos.description,
        }),
        ...(timeFeePos && {
          time_fee: Number(timeFeePos.price),
          timeFeeTitle: timeFeePos.name,
          timeFeeDescription: timeFeePos.description,
        }),
        ...(specialServicesPos && {
          special_services: Number(specialServicesPos.price),
          specialServicesTitle: specialServicesPos.name,
          specialServicesDescription: specialServicesPos.description,
        }),
        ...(additionalServicesPos && {
          additional_services: Number(additionalServicesPos.price),
          additionalServicesTitle: additionalServicesPos.name,
          additionalServicesDescription: additionalServicesPos.description,
        }),
        ...(incidentalCostsPos && {
          incidental_costs: Number(incidentalCostsPos.amount) * 100,
          incidentalCostsTitle: incidentalCostsPos.name,
          incidentalCostsDescription: incidentalCostsPos.description,
        }),
      };
    }

    function onEditHOAI(item) {
      const hoaiData = hoais.find((el) => item.hoaiId === el.id);
      const exoPositions = allItems.filter((el) => item.hoaiId === el.hoaiId);
      if (hoaiData && Object.keys(hoaiData).length > 0) {
        const mergedPositions = mergeExoPositionsIntoApiPositions(
          hoaiData.positions,
          exoPositions,
        );
        const additionalCosts =
          parseAdditionalCostsFromExoPositions(exoPositions);
        setOpenHOAI({
          ...hoaiData,
          ...additionalCosts,
          hoaiTitle: item.name,
          positions: mergedPositions,
        });
      } else setOpenHOAI({ id: item.hoaiId });
    }

    function mergeExoPositionsIntoApiPositions(apiPositions, exoPositions) {
      if (!apiPositions || apiPositions.length === 0) return apiPositions;

      const phaseItems = exoPositions.filter((p) => p.hoaiType === 'phase');
      const siemonItems = exoPositions.filter(
        (p) => p.hoaiType === 'siemonPhase',
      );

      return apiPositions.map((apiPos, index) => {
        const matchingExo = phaseItems.find((p) => p.phaseNumber === index + 1);
        // Phase was deleted in ExoPositions if no matching item found
        const isDeleted =
          !matchingExo && !siemonItems.some((p) => p.phaseNumber === index + 1);

        const merged = {
          ...apiPos,
          disabled: isDeleted ? true : apiPos.disabled,
        };

        if (matchingExo) {
          // Update title from ExoPositions if changed
          if (matchingExo.name) {
            merged.title = matchingExo.name;
          }
          // Update description from ExoPositions
          merged.description = matchingExo.description ?? apiPos.description;
          // Update value (percentage) from ExoPositions
          if (matchingExo.amount != null) {
            merged.value = String(
              Math.round(Number(matchingExo.amount) * 100 * 100) / 100,
            );
          }
          // For flat positions, derive phaseHonorarium from price * amount
          if (
            matchingExo.isFlat ||
            matchingExo.hoaiPosition?.type === 'flat' ||
            apiPos.type === 'flat'
          ) {
            const netto =
              Number(matchingExo.price || 0) * Number(matchingExo.amount || 0);
            merged.phaseHonorarium = String(Math.round(netto * 100) / 100);
          }
        }

        // Merge siemon positions
        if (apiPos.siemonPositions && apiPos.siemonPositions.length > 0) {
          const phaseSiemons = siemonItems.filter(
            (p) => p.phaseNumber === index + 1,
          );
          if (phaseSiemons.length > 0) {
            merged.siemonPositions = apiPos.siemonPositions.map(
              (apiSiemon, sIndex) => {
                const matchingSiemon = phaseSiemons[sIndex];
                if (matchingSiemon) {
                  return {
                    ...apiSiemon,
                    title: matchingSiemon.name || apiSiemon.title,
                    description:
                      matchingSiemon.description ?? apiSiemon.description,
                    value:
                      matchingSiemon.amount != null
                        ? String(
                            Math.round(
                              Number(matchingSiemon.amount) * 100 * 100,
                            ) / 100,
                          )
                        : apiSiemon.value,
                  };
                }
                return apiSiemon;
              },
            );
          }
        }

        return merged;
      });
    }

    // Time billing edit functions
    function onEditTimeBilling(item) {
      setSelectedTimeBillingPosition(item);
      setOpenTimeBillingEdit(true);
    }

    function handleTimeBillingSave({ position, adjustments }) {
      // 1. Update the position in allItems
      const updatedAllItems = allItems.map((p) => {
        if (p.keyId === position.keyId) {
          return position;
        }
        return p;
      });

      // 2. Update timeTrackingData with new adjustments
      // IDs are never removed — excluded entries are only marked via
      // exclude_from_report in adjustments and skipped during rendering/calculation
      if (timeTrackingData && onTimeTrackingDataChange) {
        const oldIds = timeTrackingData.ids || [];
        const oldAdjustments = timeTrackingData.adjustments || {};

        // Build new adjustments object preserving existing and updating changed ones
        const newAdjustments = {};
        oldIds.forEach((id) => {
          const adj = adjustments[id] || adjustments[String(id)];
          const oldAdj = oldAdjustments[id] || oldAdjustments[String(id)] || {};
          if (adj) {
            newAdjustments[id] = {
              comment: adj.comment !== undefined ? adj.comment : oldAdj.comment,
              time_start:
                adj.time_start !== undefined
                  ? adj.time_start
                  : oldAdj.time_start,
              time_end:
                adj.time_end !== undefined ? adj.time_end : oldAdj.time_end,
              exclude_from_report:
                adj.exclude_from_report ?? oldAdj.exclude_from_report ?? false,
            };
          } else if (Object.keys(oldAdj).length > 0) {
            newAdjustments[id] = oldAdj;
          }
        });

        // Update billing config wagePresets hours (without removing IDs)
        const billingConfig = timeTrackingData.billingConfig || {};
        const newWagePresets = (billingConfig.wagePresets || []).map(
          (preset) => {
            const updatedHours =
              preset.id &&
              position.wagePresetId &&
              String(preset.id) === String(position.wagePresetId)
                ? parseFloat(position.amount)
                : preset.hours;
            return {
              ...preset,
              hours: parseFloat(updatedHours) || 0,
            };
          },
        );

        const newTimeTrackingData = {
          ...timeTrackingData,
          ids: oldIds,
          adjustments: newAdjustments,
          billingConfig: {
            ...billingConfig,
            wagePresets: newWagePresets,
          },
        };

        onTimeTrackingDataChange(newTimeTrackingData);
      }

      // 3. Update items and trigger position change
      // Use triggerPositionChange=false to avoid handlePositionChange overwriting
      // the just-updated timeTrackingData with stale closure values
      setItems(calculateNewPositions(updatedAllItems, true, false));
      const cleanedPos = updatedAllItems.filter(
        (el) => !checkSpecialPositions(el),
      );
      onPositionChange(cleanedPos);
      debug && console.log('Positions changed:', cleanedPos);
      setOpenTimeBillingEdit(false);
      setSelectedTimeBillingPosition(null);
    }

    function processSiemonTitle(title, threshold = 6) {
      const words = title.trim().split(/\s+/);

      if (words.length <= threshold) {
        return { title, description: '' };
      }

      const truncated = words.slice(0, threshold).join(' ');
      return { title: truncated + '...', description: title };
    }

    function handleNewHOAI(data) {
      const oldHoaiId = data.oldHoaiId || data.id;
      const oldPosition = allItems.find((el) => el.hoaiId == oldHoaiId);

      const oldPositionIndex = oldPosition
        ? allItems.findIndex((el) => oldPosition.keyId === el.keyId)
        : null;

      // Collect old HOAI positions before removing, keyed by hoaiType + name
      const oldHoaiPositions = allItems.filter(
        (item) => item.hoaiId == oldHoaiId,
      );
      const oldPositionMap = new Map();
      oldHoaiPositions.forEach((pos) => {
        const key = `${pos.hoaiType}::${pos[groupKey] || pos.name || ''}`;
        if (!oldPositionMap.has(key)) {
          oldPositionMap.set(key, []);
        }
        oldPositionMap.get(key).push(pos);
      });

      var newPositions = [];
      // Support both new API format (top-level fields) and legacy (hoaiData nested)
      const hoaiData = data.hoaiData || {};
      const baseHonorarium = data.baseHonorarium ?? hoaiData.baseHonorarium;
      const apiPositions = data.positions || [];
      const frontendPhases = data.phases || [];
      const rawPhases =
        apiPositions.length > 0 ? apiPositions : hoaiData.phases || [];
      // Enrich API positions with frontend phase data for fields the backend may not return yet
      const phases = rawPhases.map((phase, idx) => {
        const fe = frontendPhases[idx];
        if (!fe) return phase;

        // Merge siemon data: use API siemonPositions as base, enrich with frontend siemon values
        let siemonData = phase.siemonPositions || phase.siemon;
        if (fe.siemon && fe.siemon.length > 0) {
          if (siemonData && siemonData.length > 0) {
            siemonData = siemonData.map((s, sIdx) => {
              const feSiemon = fe.siemon[sIdx];
              if (!feSiemon) return s;
              return {
                ...s,
                value: s.value ?? feSiemon.siemonValue,
                siemonValue: s.value ?? feSiemon.siemonValue ?? s.siemonValue,
              };
            });
          } else {
            siemonData = fe.siemon;
          }
        }

        return {
          ...phase,
          siemonPositions: phase.siemonPositions
            ? siemonData
            : phase.siemonPositions,
          siemon: siemonData,
          flatValue:
            phase.type === 'flat'
              ? Number(
                  phase.phaseHonorarium ??
                    phase.phase_honorarium ??
                    fe.flatValue,
                ) || 0
              : undefined,
          type: phase.type ?? fe.type,
        };
      });
      const vat = data.vat ?? hoaiData.vat;
      const label = data.label ?? hoaiData.label;
      const surcharge =
        data.surcharge ?? data.surchargeAmount ?? hoaiData.surcharge;
      const timeFee = data.time_fee ?? data.timeFee ?? hoaiData.timeFee;
      const specialServices =
        data.special_services ??
        data.specialServices ??
        hoaiData.specialServices;
      const additionalServices =
        data.additional_services ??
        data.additionalServices ??
        hoaiData.additionalServices;
      const incidentalCosts =
        data.incidental_costs ??
        data.incidentalCosts ??
        hoaiData.incidentalCosts;
      const showSiemon =
        data.showSiemon ??
        hoaiData.showSiemon ??
        rawPhases.some((p) => (p.siemonPositions || p.siemon)?.length > 0);

      var hoaiGroup = {
        [groupKey]: data.hoaiTitle ?? data.title,
        isGroup: true,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
        editableHOAI: true,
        hoaiId: data.id,
        depth: 0,
        hoaiType: 'group',
      };
      newPositions.push(hoaiGroup);

      let groupCounter = 1;

      var newGroup = {
        [groupKey]: t('Phases') + ' - ' + label,
        isGroup: true,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
        hoaiId: data.id,
        depth: groupCounter,
        hoaiType: 'phasesGroup',
      };
      newPositions.push(newGroup);

      phases.forEach((phase, index) => {
        // API positions use `disabled`, legacy uses `disabled` (renamed from isCrossed)
        if (phase.disabled) return;

        // API positions have `siemonPositions`, legacy has `siemon`
        const siemonData = phase.siemonPositions || phase.siemon;
        const hasSiemon = siemonData && siemonData.length > 0;
        // API positions have `maxPercent`, legacy has `maxPercent`
        const phaseValue =
          phase.value != null ? Number(phase.value) : Number(phase.maxPercent);
        const phaseTitle = phase.title;

        if (hasSiemon && showSiemon && phase.type !== 'flat') {
          newPositions.push({
            [groupKey]: phaseTitle,
            isGroup: true,
            amount: 0,
            unit: '',
            price: 0,
            taxRate: 0,
            positionableId: null,
            positionableType: 'custom',
            taskTitle: '',
            pricePerHour: '',
            monthlyLimit: 0,
            status: 0,
            hoaiId: data.id,
            hoaiPosition: phase,
            phaseNumber: index + 1,
            depth: groupCounter + 1,
            hoaiType: 'siemonGroup',
          });

          siemonData.forEach((siemon) => {
            // API format uses `value`/`maxPercent`, legacy uses `siemonValue`/`maxPercent`
            const siemonValue =
              siemon.value ?? siemon.siemonValue ?? siemon.maxPercent;
            const { title, description } = processSiemonTitle(siemon.title);
            newPositions.push({
              ...defaultPosition,
              price: baseHonorarium,
              amount: Number(siemonValue) / 100,
              name: `${title}`,
              taxRate: vat ?? defaultPosition.taxRate,
              hoaiId: data.id,
              hoaiPosition: phase,
              hoaiSiemonPosition: siemon,
              phaseNumber: index + 1,
              description: siemon.description ?? description,
              depth: groupCounter + 2,
              type: phase.type === 'flat' ? 'custom' : 'hoaiPhase',
              hoaiType: 'siemonPhase',
            });
          });
        } else if (phase.type === 'flat') {
          const flatValue =
            Number(
              phase.flatValue ??
                phase.phaseHonorarium ??
                phase.phase_honorarium,
            ) || 0;
          const amount = phaseValue / 100;
          // Set price so that price * amount = flatValue
          const price = amount > 0 ? flatValue / amount : 0;
          newPositions.push({
            ...defaultPosition,
            price: price,
            amount: amount,
            name: phaseTitle,
            taxRate: vat ?? defaultPosition.taxRate,
            hoaiId: data.id,
            hoaiPosition: phase,
            phaseNumber: index + 1,
            description: phase.description,
            depth: groupCounter + 1,
            type: 'hoaiPhase',
            hoaiType: 'phase',
            isFlat: true,
          });
        } else
          newPositions.push({
            ...defaultPosition,
            price: baseHonorarium,
            amount: phaseValue / 100,
            name: phaseTitle,
            taxRate: vat ?? defaultPosition.taxRate,
            hoaiId: data.id,
            hoaiPosition: phase,
            phaseNumber: index + 1,
            description: phase.description,
            depth: groupCounter + 1,
            type: 'hoaiPhase',
            hoaiType: 'phase',
          });
      });

      const surchargeTitle =
        data.surchargeTitle ?? data.surcharge_title ?? hoaiData.surchargeTitle;
      const surchargeDescription =
        data.surchargeDescription ??
        data.surcharge_description ??
        hoaiData.surchargeDescription;
      const timeFeeTitle =
        data.timeFeeTitle ?? data.time_fee_title ?? hoaiData.timeFeeTitle;
      const timeFeeDescription =
        data.timeFeeDescription ??
        data.time_fee_description ??
        hoaiData.timeFeeDescription;
      const specialServicesTitle =
        data.specialServicesTitle ??
        data.special_services_title ??
        hoaiData.specialServicesTitle;
      const specialServicesDescription =
        data.specialServicesDescription ??
        data.special_services_description ??
        hoaiData.specialServicesDescription;
      const additionalServicesTitle =
        data.additionalServicesTitle ??
        data.additional_services_title ??
        hoaiData.additionalServicesTitle;
      const additionalServicesDescription =
        data.additionalServicesDescription ??
        data.additional_services_description ??
        hoaiData.additionalServicesDescription;
      const incidentalCostsTitle =
        data.incidentalCostsTitle ??
        data.incidental_costs_title ??
        hoaiData.incidentalCostsTitle;
      const incidentalCostsDescription =
        data.incidentalCostsDescription ??
        data.incidental_costs_description ??
        hoaiData.incidentalCostsDescription;

      surcharge &&
        newPositions.push({
          ...defaultPosition,
          amount: surcharge / 100,
          name: surchargeTitle || t('Surcharge'),
          description: surchargeDescription,
          taxRate: 0,
          hoaiId: data.id,
          depth: groupCounter + 1,
          type: 'percent',
          hoaiType: 'surcharge',
        });

      var additionalCostsPositions = [];

      const additionalCostDepth = groupCounter;

      timeFee &&
        additionalCostsPositions.push({
          ...defaultPosition,
          price: timeFee,
          amount: 1,
          name: timeFeeTitle || t('Time fee'),
          description: timeFeeDescription,
          taxRate: vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: additionalCostDepth,
          hoaiType: 'timeFee',
        });
      specialServices &&
        additionalCostsPositions.push({
          ...defaultPosition,
          price: specialServices,
          amount: 1,
          name: specialServicesTitle || t('Special Services'),
          description: specialServicesDescription,
          taxRate: vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: additionalCostDepth,
          hoaiType: 'specialServices',
        });
      additionalServices &&
        additionalCostsPositions.push({
          ...defaultPosition,
          price: additionalServices,
          amount: 1,
          name: additionalServicesTitle || t('Additional Services'),
          description: additionalServicesDescription,
          taxRate: vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: additionalCostDepth,
          hoaiType: 'additionalServices',
        });
      if (incidentalCosts) {
        additionalCostsPositions.push({
          ...defaultPosition,
          amount: incidentalCosts / 100,
          name: incidentalCostsTitle || t('Incidental Costs'),
          description: incidentalCostsDescription,
          taxRate: vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: additionalCostDepth,
          type: 'percent',
          hoaiType: 'incidentalCosts',
        });
      }

      if (additionalCostsPositions.length > 0) {
        newPositions = newPositions.concat(additionalCostsPositions);
      }

      // Match new positions to old ones by hoaiType to preserve keyId/id/sortId
      // Use a queue per hoaiType so order is preserved
      const oldByType = {};
      oldHoaiPositions.forEach((pos) => {
        const t = pos.hoaiType || '';
        if (!oldByType[t]) oldByType[t] = [];
        oldByType[t].push(pos);
      });

      const maxSortId = Math.max(...allItems.map((el) => el.sortId || 0));

      var hoaiPositions = newPositions.map((newPos, idx) => {
        const t = newPos.hoaiType || '';
        const queue = oldByType[t];
        const oldMatch = queue && queue.length > 0 ? queue.shift() : null;

        return {
          ...defaultPosition,
          ...newPos,
          sortId: oldMatch?.sortId || maxSortId + idx + 1,
          keyId: oldMatch?.keyId || uuid(),
          ...(oldMatch?.id != null && { id: oldMatch.id }),
        };
      });

      let resultItems;

      if (oldHoaiPositions.length > 0) {
        const oldHoaiKeyIds = new Set(oldHoaiPositions.map((p) => p.keyId));

        // Keep the phase rows together as one contiguous block that follows
        // their phases-group, so they stay validly nested and visible.
        // Replacing each phase in place instead leaves them orphaned (and
        // hidden) whenever a surcharge/incidental line has been dragged between
        // the group and its phases. Globals (groups, surcharge, incidental, …)
        // are still matched 1:1 by hoaiType and keep their in-place positions.
        const newPhaseBlock = hoaiPositions.filter((p) => p.phaseNumber != null);
        const newGlobal = {};
        hoaiPositions.forEach((pos) => {
          if (pos.phaseNumber == null) {
            const key = pos.hoaiType || '';
            if (!newGlobal[key]) newGlobal[key] = [];
            newGlobal[key].push(pos);
          }
        });

        const usedGlobal = new Set();
        resultItems = [];
        let phaseBlockInserted = false;
        let lastHoaiIdx = -1;

        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];
          if (!oldHoaiKeyIds.has(item.keyId)) {
            // Non-HOAI position — keep in place
            resultItems.push(item);
            continue;
          }
          if (item.phaseNumber != null) {
            // Old phase row — the phase block is emitted after its group below
            lastHoaiIdx = resultItems.length;
            continue;
          }
          // Global HOAI position (group, phasesGroup, surcharge, etc.)
          const queue = newGlobal[item.hoaiType || ''];
          const replacement = queue
            ? queue.find((p) => !usedGlobal.has(p))
            : null;
          if (replacement) {
            usedGlobal.add(replacement);
            resultItems.push(replacement);
            // Emit the phase block immediately after its phases-group.
            if (
              !phaseBlockInserted &&
              replacement.hoaiType === 'phasesGroup' &&
              newPhaseBlock.length > 0
            ) {
              resultItems.push(...newPhaseBlock);
              phaseBlockInserted = true;
            }
          }
          // else: removed — skip
          lastHoaiIdx = resultItems.length;
        }

        // Fallback for a flat HOAI without a phases-group: drop the phase block
        // where the old phase rows were.
        if (!phaseBlockInserted && newPhaseBlock.length > 0) {
          const at = lastHoaiIdx >= 0 ? lastHoaiIdx : resultItems.length;
          resultItems.splice(at, 0, ...newPhaseBlock);
        }
        // Insert any globals that weren't matched to an old position.
        const remainingGlobals = Object.values(newGlobal)
          .flat()
          .filter((p) => !usedGlobal.has(p));
        if (remainingGlobals.length > 0) {
          const at = lastHoaiIdx >= 0 ? lastHoaiIdx : resultItems.length;
          resultItems.splice(at, 0, ...remainingGlobals);
        }
      } else {
        // No existing HOAI positions — insert before the post-position divider
        resultItems = [...allItems];
        // Remove empty first row if present
        if (resultItems.length >= 4 && resultItems[0].name == '') {
          resultItems = resultItems.slice(1);
        }
        // Find the divider and insert before it
        const dividerIndex = resultItems.findIndex((el) => el.isPostPosDivider);
        if (dividerIndex >= 0) {
          resultItems.splice(dividerIndex, 0, ...hoaiPositions);
        } else {
          resultItems = [...resultItems, ...hoaiPositions];
        }
      }

      const newHoais = updateOrCreate(hoais, data);
      setHoais(newHoais);

      // Recalculate sortIds from the items
      const newSortIdsList = resultItems.map((item) => item.sortId);
      setSortIds(newSortIdsList);
      // Pass false for triggerPositionChange — we call handlePositionChange manually with hoaiData
      const newOrderedPositions = calculateNewPositions(
        resultItems,
        true,
        false,
      );

      handlePositionChange(resultItems, newHoais);
      setItems(newOrderedPositions);
    }

    itemsRef.current = items;

    async function handleAiSort() {
      if (aiSorting || !aiScanDocument) return;
      let sortRequest;
      try {
        sortRequest = buildSortRequest(itemsRef.current, aiScanDocument);
      } catch {
        setAiSortNotice({
          severity: 'warning',
          text: t('Too many positions for the AI sort.'),
        });
        return;
      }
      if (sortRequest.refs.length < 2) {
        setAiSortNotice({
          severity: 'info',
          text: t('At least two positions are needed for the AI sort.'),
        });
        return;
      }

      setAiSorting(true);
      try {
        const arrangement = await runAiPositionSort({
          config,
          document: aiScanDocument,
          prompt: sortRequest.prompt,
          refs: sortRequest.refs,
          groupRefs: sortRequest.groupRefs,
          projectId: project?.id ?? null,
        });
        // the rows of now: the user may have edited while the AI worked
        const newItems = applyArrangement(
          itemsRef.current,
          arrangement.items,
          sortRequest.snapshot,
          { groupDefaults: defaultPosition, newKeyId: uuid },
        ).map((row, index) => ({ ...row, sortId: index + 1 }));
        setSortIds(newItems.map((row) => row.sortId));
        const ordered = calculateNewPositions(newItems, true, true);
        handlePositionChange(ordered);
        setItems(ordered);
        setAiSortNotice({
          severity: 'success',
          text: t('The AI sorted the positions.'),
        });
      } catch (err) {
        console.error('AI sort failed', err);
        setAiSortNotice({
          severity: 'error',
          text:
            err?.message === 'no_arrangement'
              ? t('The AI did not return a new order.')
              : t('The AI sort failed. Please try again.'),
        });
      } finally {
        setAiSorting(false);
      }
    }

    function handleImportPositions(positions) {
      const postPos = positions.filter((el) => el.isPostPosition);
      positions = positions.filter((el) => !el.isPostPosition);

      const maxSortId = Math.max(...items.map((el) => el.sortId));
      var newSortIdsList = [];

      //Normal positions
      var newPositions = [];
      positions.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        newPositions.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: newPos.keyId ?? uuid(),
        });
      });

      const newOrderedPositions = insertPositions(
        items,
        newPositions,
        newSortIdsList,
      );

      //Post positions
      newPositions = [];
      newSortIdsList = [];
      postPos.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        newPositions.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: newPos.keyId ?? uuid(),
        });
      });

      const finalOrderedPositions = insertPositions(
        newOrderedPositions,
        newPositions,
        newSortIdsList,
        true,
      );

      handlePositionChange(finalOrderedPositions);
      setItems(finalOrderedPositions);
      setOpenImport(false);
    }

    function handleNewPositions(
      positionsToAdd,
      afterDivider,
      timeTrackingData = null,
    ) {
      const maxSortId = Math.max(...items.map((el) => el.sortId));

      const newSortIdsList = [];

      var newPositionsToAdd = [];
      positionsToAdd.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        newPositionsToAdd.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: uuid(),
        });
      });

      const newOrderedPositions = insertPositions(
        items,
        newPositionsToAdd,
        newSortIdsList,
        afterDivider,
      );

      handlePositionChange(newOrderedPositions);
      setItems(newOrderedPositions);

      // Pass time tracking data to parent if provided
      if (timeTrackingData && onTimeTrackingDataChange) {
        onTimeTrackingDataChange(timeTrackingData);
      }
    }

    const mouseSensor = useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    });
    const touchSensor = useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
        distance: 10,
      },
    });
    const sensors = useSensors(mouseSensor, touchSensor);

    const clamp = (value, min, max) =>
      value < min ? min : value > max ? max : value;

    function calculateDepthFromDelta(
      horizontalDelta,
      originalDepth,
      [prev, next],
    ) {
      const { depth: prevDepth, isGroup } = prev;
      const { depth: nextDepth } = next;

      const offset = Math.round(horizontalDelta / posColWidth);
      const tentativeDepth = originalDepth + offset;

      const maxDepth = isGroup
        ? prevDepth + 1
        : prevDepth === -1
          ? 0
          : prevDepth;

      return clamp(tentativeDepth, nextDepth, maxDepth);
    }

    function customOffset({ activatorEvent, draggingNodeRect, transform }) {
      return {
        ...transform,
        x: transform.x + 100,
      };
    }

    const handleKeyDown = useCallback((e) => {
      const virtuoso = virtuosoRef.current;
      if (!virtuoso) return;

      const SCROLL_STEP = 60; //scroll step size

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        virtuoso.scrollBy({ top: SCROLL_STEP, behavior: 'smooth' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        virtuoso.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' });
      }
    }, []);

    // Attach global listener
    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
      <Box className="w-full h-full flex flex-col overflow-hidden">
        <ExoDialog open={openHOAI}>
          <Box className="p-10">
            <HOAICalculator
              onClose={() => setOpenHOAI(null)}
              onSubmit={handleNewHOAI}
              startDataCollection={openHOAI}
              hoaiTitle={openHOAI?.title ?? title}
            />
          </Box>
        </ExoDialog>
        <DndContext
          onDragEnd={handleOnDragEnd}
          onDragOver={handleOnDragOver}
          onDragStart={handleOnDragStart}
          onDragMove={handleOnDragMove}
          sensors={sensors}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={sortIds}
            strategy={verticalListSortingStrategy}
            useDragOverlay={true}
            disabled={
              items.filter((el) => !checkSpecialPositions(el)).length <= 1
            }
          >
            <Box
              className={`${className} relative ${
                activeItem ? 'select-none' : ''
              }`}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                },
                '& .MuiAccordionSummary-root': {
                  minHeight: '26px',
                },
                '& th:first-child': {
                  borderTopLeftRadius: '8px',
                },
                '& th:last-child': {
                  borderTopRightRadius: '8px',
                },
                '& tr:last-child>td:first-child': {
                  borderBottomLeftRadius: '8px',
                },
                '& tr:last-child>td:last-child': {
                  borderBottomRightRadius: '8px',
                },
                '& td, th': {
                  backgroundColor: colors.bgInput,
                },
                '& .position-group td': {
                  backgroundColor: colors.selected,
                },
                '& .order-position td': {
                  background: `linear-gradient(${colors.selected}40, ${colors.selected}40), linear-gradient(${colors.bgInput}, ${colors.bgInput})`,
                },
              }}
            >
              <Box className="w-full h-full overflow-hidden flex flex-col">
                <TableVirtuoso
                  ref={virtuosoRef}
                  context={{
                    items,
                    activeItem,
                    projectedIndex,
                    dynamicColumns,
                    horizontalDelta,
                    posColWidth,
                    groupKey,
                    collapseModules,
                    imageType,
                    imageApiFilter,
                    descriptionVars,
                    showVars,
                    openAmount,
                    calculateDepthFromDelta,
                    onFocus,
                    setItems,
                    calculateNewPositions,
                    handlePositionChange,
                    onEditHOAI,
                    onEditTimeBilling,
                    handleDelete,
                    handleDuplicate,
                    handleNewPosition,
                    timeTrackingData,

                    hideGroups: Boolean(hideGroups),
                    hideSum: Boolean(hideSum),
                    hideServices: Boolean(hideServices),
                    hideProducts: Boolean(hideProducts),
                    hideNewPosition: Boolean(hideNewPosition),
                    hideDiscount: Boolean(hideDiscount),
                    hideHOAI: hideHOAI,
                    hideTimeBilling: hideTimeBilling,
                    hidePostPositions: hidePostPositions,
                    hideImport: hideImport,
                    cols: dynamicColumns,
                    onNewGroup: handleNewGroup,
                    onNewPosition: (postPosition) =>
                      handleNewPosition(defaultPosition, postPosition),
                    onSelectProduct: (postPosition) =>
                      setOpenProducts(
                        postPosition ? 'afterDivider' : 'beforeDivider',
                      ),
                    onSelectService: (postPosition) =>
                      setOpenServices(
                        postPosition ? 'afterDivider' : 'beforeDivider',
                      ),
                    onNewDiscount: () => setOpenDiscount(!openDiscount),
                    onNewPercent: (postPosition) =>
                      handleNewPosition(
                        {
                          ...defaultPosition,
                          type: 'percent',
                        },
                        postPosition,
                      ),
                    onNewHOAI: () => setOpenHOAI({ defaultTaxRate }),
                    onNewTimeBilling: (postPosition) =>
                      setOpenTimeBilling(
                        postPosition ? 'afterDivider' : 'beforeDivider',
                      ),
                    onImport: () => setOpenImport(true),
                    onSortWithAi: aiScanDocument ? handleAiSort : null,
                    aiSorting,
                    sum: sum,
                    skonto: skonto,
                  }}
                  //followOutput={items?.length > 5}
                  followOutput={false}
                  style={{ width: '100%', height: 'calc(100vh - 260px)' }}
                  data={items}
                  defaultItemHeight={10}
                  fixedHeaderContent={() => (
                    <PositionsHeader
                      collapseModules={collapseModules}
                      columns={dynamicColumns}
                    />
                  )}
                  itemContent={(index, item, context) => null}
                  components={{
                    Table: TableComponent,
                    TableRow: TableRowComponent,
                  }}
                />
              </Box>
              {createPortal(
                <DragOverlay modifiers={[customOffset]} zIndex={9999}>
                  {activeItem ? (
                    <PositionsPlaceholder
                      collapseModules={collapseModules}
                      index={activeItem.index}
                      items={items}
                      item={activeItem}
                      columns={dynamicColumns}
                      imageType={imageType}
                      imageApiFilter={imageApiFilter}
                      descriptionVars={descriptionVars}
                      showVars={showVars}
                      openAmount={openAmount}
                    />
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </Box>
          </SortableContext>
        </DndContext>

        <ExoDialog
          open={Boolean(openProducts)}
          onClose={() => setOpenProducts(null)}
        >
          <SelectProduct
            onSelect={(product) =>
              handleProductSelect(product, openProducts === 'afterDivider')
            }
            onClose={() => setOpenProducts(null)}
          />
        </ExoDialog>
        <ExoDialog
          open={Boolean(openServices)}
          onClose={() => setOpenServices(null)}
        >
          <SelectService
            onSelect={(service) =>
              handleServiceSelect(service, openServices === 'afterDivider')
            }
            onClose={() => setOpenServices(null)}
          />
        </ExoDialog>
        {openDiscount && (
          <PositionDiscount
            sum={sum}
            open={openDiscount}
            onClose={() => setOpenDiscount(false)}
            onCreate={handleNewDiscount}
          />
        )}
        <ExoDialog
          open={Boolean(openTimeBilling)}
          onClose={() => setOpenTimeBilling(null)}
          limitWidth
          maxWidth="1100px"
        >
          <SelectTimeBilling
            onAddPositions={(pos, timeTrackingData) =>
              handleNewPositions(
                pos,
                openTimeBilling === 'afterDivider',
                timeTrackingData,
              )
            }
            onClose={() => setOpenTimeBilling(null)}
            initialProject={project}
          />
        </ExoDialog>
        <SelectTimeBillingEdit
          open={openTimeBillingEdit}
          onClose={() => {
            setOpenTimeBillingEdit(false);
            setSelectedTimeBillingPosition(null);
          }}
          position={selectedTimeBillingPosition}
          timeTrackingData={timeTrackingData}
          onSave={handleTimeBillingSave}
        />
        <ExoDialog open={openImport} onClose={() => setOpenImport(false)}>
          {openImport && (
            <SelectImportPositions
              onSubmit={handleImportPositions}
              aiScanDocument={aiScanDocument}
              project={project}
              defaultTaxRate={defaultPosition?.taxRate}
              onCancel={() => setOpenImport(false)}
            />
          )}
        </ExoDialog>
        <Snackbar
          open={Boolean(aiSortNotice)}
          autoHideDuration={6000}
          onClose={() => setAiSortNotice(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={aiSortNotice?.severity || 'info'}
            onClose={() => setAiSortNotice(null)}
          >
            {aiSortNotice?.text}
          </Alert>
        </Snackbar>
        <Dialog open={alertState.alertOpen}>
          <Alert
            severity={'warning'}
            className=" max-w-full w-96 flex flex-col items-center gap-2"
            sx={{
              bgcolor: 'transparent',
              border: `1px solid ${colors.info}80`,
              '.MuiAlert-icon': {
                margin: 0,
                svg: {
                  width: '50px',
                  height: '50px',
                },
              },
            }}
          >
            {
              <Typography className="w-full text-center">
                {t('Also delete all positions belonging to this group?')}
              </Typography>
            }

            <Box
              className="flex flex-row justify-between mt-4 w-full gap-6"
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Button
                onClick={() => setAlertState({ alertOpen: false, item: null })}
                color="error"
                variant="outlined"
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleGroupDelete(alertState.item, false)}
              >
                {t('No')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleGroupDelete(alertState.item, true)}
              >
                {t('Yes')}
              </Button>
            </Box>
          </Alert>
        </Dialog>
        <ExoAlert
          open={timeBillingWarning}
          onClose={() => setTimeBillingWarning(false)}
          alert={{
            type: 'warning',
            message: t(
              'Time billing data was reconstructed. Adjustments to times and changes to comments could not be loaded.',
            ),
            cancelLabel: t('OK'),
          }}
        />
      </Box>
    );
  },
);

export default ExoPositions;

const PositionsPlaceholder = ({
  item,
  items,
  columns,
  collapseModules,
  imageType,
  imageApiFilter,
  index,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <table>
      <tbody>
        <tr
          className={item.orderId ? 'order-position' : ''}
          style={{
            background: item.isGroup ? colors.selected : colors.bgInput,
            opacity: 0.7,
          }}
        >
          {collapseModules && <td className="w-[37px]"></td>}
          {columns.map(
            (col, i) =>
              item.keyId && (
                <Entry
                  key={'' + item.keyId + item.id + i}
                  imageType={imageType}
                  imageApiFilter={imageApiFilter}
                  col={col}
                  item={items[index]}
                  onChange={() => {}}
                >
                  {item[col.key]}
                </Entry>
              ),
          )}
        </tr>
      </tbody>
    </table>
  );
};

const TableComponent = (props) => (
  <table {...props} style={{ width: '100%' }} />
);

const TableRowComponent = ({ context, ...props }) => {
  const { 'data-index': index } = props;
  const item = context.items[index];

  return context.items.length != 0 ? (
    item.isSum ? (
      <>
        {context.hidePostPositions && (
          <Controls
            hideGroups={context.hideGroups}
            hideSum={context.hideSum}
            hideServices={context.hideServices}
            hideProducts={context.hideProducts}
            hideNewPosition={context.hideNewPosition}
            hideDiscount={context.hideDiscount}
            hideHOAI={context.hideHOAI}
            hideTimeBilling={context.hideTimeBilling}
            hidePostPos={context.hidePostPositions}
            hideImport={context.hideImport}
            cols={context.cols}
            onNewGroup={context.onNewGroup}
            onNewPosition={context.onNewPosition}
            onSelectProduct={context.onSelectProduct}
            onSelectService={context.onSelectService}
            onNewDiscount={context.onNewDiscount}
            onNewPercent={context.onNewPercent}
            onNewHOAI={context.onNewHOAI}
            onNewTimeBilling={context.onNewTimeBilling}
            onImport={context.onImport}
            onSortWithAi={context.onSortWithAi}
            aiSorting={context.aiSorting}
          />
        )}
        {!context.hideSum && (
          <Sum
            sum={context.sum}
            cols={context.dynamicColumns}
            collapseModules={context.collapseModules}
            skonto={context.skonto}
          />
        )}
      </>
    ) : (
      <Position
        key={item.keyId}
        props={props}
        index={index}
        item={item}
        context={context}
      />
    )
  ) : (
    <PositionsRowEmpty />
  );
};
